import { MeiliSearch, type MultiSearchResponse } from 'meilisearch'

import config from '../../config'
import { SEARCH_RESULT_TYPE, type CompanySearchResult, type DocumentSearchResult, type EventSearchResult, type SearchResults, type SingleListSearchResponse, type VideoSearchResult } from './types'

const meiliSearchClient = new MeiliSearch({
  host: config.VITE_SEARCH_HOST,
  apiKey: config.VITE_SEARCH_KEY,
})

const search = async (text: string, index?: SEARCH_HITS_INDEX, filter?: string | string[], sort?: string[]): Promise<SearchResults> => {
  let results
  if(!index) {
    results = await meiliSearchClient.multiSearch({
      queries: [
        { showRankingScore: true, indexUid: SEARCH_HITS_INDEX.VIDEOS, q: text, limit: 5 },
        { showRankingScore: true, indexUid: SEARCH_HITS_INDEX.DOCUMENTS, q: text, limit: 5 },
        { showRankingScore: true, indexUid: SEARCH_HITS_INDEX.EVENTS, q: text, limit: 2 },
        { showRankingScore: true, indexUid: SEARCH_HITS_INDEX.COMPANIES, q: text, limit: 3 },
      ]
    })
  } else {
    results = await meiliSearchClient.multiSearch({
      queries: [
        // Until we have pagination in place, I increased the limit to 200
        { indexUid: index, q: text, filter, sort, showRankingScore: true, limit: 200 },
      ]
    })
  }
  const parsedResults = parseSearchResults(results)
  return parsedResults
}

const parseSearchResults = (response: MultiSearchResponse): SearchResults => {
  const results: SearchResults = {
    videos: response.results
      .find(resultIndex => resultIndex.indexUid === SEARCH_HITS_INDEX.VIDEOS)?.hits
        .map((video) => ({ ...video, _type: SEARCH_RESULT_TYPE.VIDEO })) as Array<VideoSearchResult>,
      documents: response.results
      .find(resultIndex => resultIndex.indexUid === SEARCH_HITS_INDEX.DOCUMENTS)?.hits
        .map((video) => ({ ...video, _type: SEARCH_RESULT_TYPE.DOCUMENT })) as Array<DocumentSearchResult>,
      events: response.results.find(resultIndex => resultIndex.indexUid === SEARCH_HITS_INDEX.EVENTS)?.hits
        .map((video) => ({ ...video, _type: SEARCH_RESULT_TYPE.EVENT })) as Array<EventSearchResult>,
      companies: response.results.find(resultIndex => resultIndex.indexUid === SEARCH_HITS_INDEX.COMPANIES)?.hits
        .map((video) => ({ ...video, _type: SEARCH_RESULT_TYPE.COMPANY })) as Array<CompanySearchResult>,
  }
  results.all = orderByRankingScore(results)
  return results
}

export const orderByRankingScore = (results: SearchResults): SingleListSearchResponse => {
  // combine all results into a single array and sort by ranking score
  let allResults: SingleListSearchResponse = []
  allResults = allResults.concat(results.videos, results.documents, results.events, results.companies)
  allResults = allResults.sort((a, b) => b._rankingScore - a._rankingScore)
  return allResults
}

export enum SEARCH_HITS_INDEX {
  VIDEOS = 'videos',
  DOCUMENTS = 'documents',
  EVENTS = 'events',
  COMPANIES = 'companies',
}

export const generateFilterClause = (filterName: string, arr: string[] | undefined): string => {
  if (!arr || arr.length === 0) return ''

  if (arr.length === 1) {
    return `${filterName} = ${arr[0]}`
  }

  return arr.map(id => `${filterName} = ${id}`).join(' OR ')
}

export const generateSortClause = (field: string | undefined, order: 'asc' | 'desc' = 'desc'): string[] => {
  if (!field) {
    return [] // Default sorting will be handled by Meilisearch's ranking rules
  }
  return [`${field}:${order}`]
}



export default search


